import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Bilingual from day one.
 *
 * v2's fatal shortcut was hard-coding English inside the render functions, so
 * the Thai toggle changed the sidebar and almost nothing else. Every
 * user-visible string in v3 comes from here. If a string isn't in this file, it
 * doesn't belong in the JSX.
 *
 * Thai copy should read like a Thai colleague wrote it, not like a translation
 * of the English — a few keys below are deliberately not literal.
 */

export const LANG = {
  en: {
    // shell
    search: 'Search anything',
    logDeal: 'Log a deal',
    logService: 'Log service',
    quickLog: 'Quick log',
    signOut: 'Sign out',
    language: 'Language',
    appearance: 'Appearance',
    accent: 'Accent',
    viewAs: 'View as',
    light: 'Light',
    dark: 'Dark',
    compact: 'Compact',
    comfortable: 'Comfortable',
    viewOnly: 'View only',

    // nav
    navToday: 'Today',
    navDeals: 'New Customer Deals',
    navCalls: 'Cold Call',
    navClients: 'Leads',
    navAccounts: 'Accounts',
    navUpsells: 'Upsell Deals',
    navReview: 'Monthly Review',
    navInsights: 'Insights',
    navAdmin: 'Admin',

    // sign in
    signIn: 'Sign in',
    // All three Workspaces, because all three are in `allowed_domains` — and
    // motionreach.co.th is the one the admin account itself uses. Listing only
    // two told a MotionReach colleague their own address would not work.
    signInBlurb: 'Use your brandstorm.co.th, brandstorm.global or motionreach.co.th Google account.',
    continueGoogle: 'Continue with Google',
    or: 'or',
    workEmail: 'Work email',
    magicLink: 'Email me a magic link',
    magicNote: 'Magic links work for accounts outside the Workspace. Sessions last 30 days.',
    magicSent: 'If that address is enrolled, a sign-in link is on its way.',
    sessionNotKept:
      'Google signed you in, but this browser did not keep the session cookie. Check that cookies are allowed for crm.motionreach.co.th — then try again.',
    tagline: 'The funnel is the product.',
    taglineSub: 'One pipeline for both houses — briefs in, revenue out.',

    // today
    morning: 'Morning',
    myAttainment: 'My attainment',
    teamAttainment: 'Team attainment',
    monthlyReview: 'Monthly review',
    ofQuota: 'of {q} quota',
    noQuota: 'No quota set',
    newCust: 'New customer',
    upsell: 'Upsell',
    toGraduate: 'Won — waiting to graduate',
    graduate: 'Graduate',
    newBriefs: 'New briefs',
    createDeal: 'Create deal',
    briefsZero: 'Inbox zero — every brief has a deal.',
    callsDue: 'Calls due today',
    logCall: 'Log call',
    needsAttention: 'Needs attention',
    servicingDue: 'Servicing due',
    myTasks: 'My tasks',
    idle: '{n}d idle',
    nothingWaiting: 'Nothing waiting to graduate.',
    noCallsDue: 'No calls queued. Go find some.',
    allMoving: 'Nothing is going stale. Good.',
    noServicing: 'No accounts need a touch today.',
    tasksClear: 'No open tasks. Enjoy it.',

    // deals
    board: 'Board',
    table: 'Table',
    allOwners: 'All owners',
    allTiers: 'All tiers',
    openPipeline: 'Open pipeline',
    deal: 'Deal',
    titleClient: 'Title · client',
    salesRep: 'Sales rep',
    amount: 'Amount',
    close: 'Close',
    stage: 'Stage',
    noDeals: 'No deals here — move one across.',
    moveTo: 'Move to',
    lossReason: 'Why was it lost?',
    lossRequired: 'A loss needs a reason.',
    competitor: 'Competitor',
    cancel: 'Cancel',
    save: 'Save',
    markLost: 'Mark lost',

    // drawers
    overview: 'Overview',
    activity: 'Activity',
    quotes: 'Quotes',
    contacts: 'Contacts',
    projects: 'Projects',
    tasks: 'Tasks',
    owner: 'Owner',
    aeOwner: 'AE',
    contact: 'Primary contact',
    source: 'Source',
    tier: 'Tier',
    weighted: 'Weighted value',
    industry: 'Industry',
    website: 'Website',
    lastActivity: 'Last activity',
    noActivity: 'Nothing logged yet.',
    noQuotes: 'No FlowAccount documents linked yet.',
    noContacts: 'No contacts yet.',
    openDeals: 'Open deals',
    revenueYtd: 'Revenue YTD',

    // quick log
    logActivity: 'Log activity',
    type: 'Type',
    call: 'Call',
    meet: 'Meeting',
    email: 'Email',
    note: 'Note',
    line: 'LINE',
    subject: 'What happened',
    details: 'Details',
    against: 'Against',
    followUp: 'Follow-up task',
    followUpPh: 'e.g. Send the media kit',
    due: 'Due',
    logIt: 'Log it',
    logged: 'Logged.',
    saved: 'Saved.',
    moved: 'Moved to {s}.',
    taskDone: 'Task done.',
    pickTarget: 'Pick a deal or a company first.',

    // cold call
    tabProspects: 'Prospects',
    tabQueue: 'Queue',
    tabCold: 'Gone cold',
    coldBlurb: 'Cold prospecting — source, call, convert',
    sources: 'Sources',
    allIndustries: 'All industries',
    importList: 'Import a list',
    importBlurb:
      'Paste one company per line, or CSV: name, industry, phone, contact. LinkedIn and event lists are exports — there is no API to pull them live.',
    generate: 'Add to prospects',
    excludesExisting: 'Excludes anyone already in Clients or in an open deal',
    company: 'Company',
    fit: 'Fit',
    whyNow: 'Signal — why now',
    contactFound: 'Contact found',
    action: 'Action',
    addToQueue: 'Add to queue',
    notAFit: 'Not a fit',
    attempts: 'Attempts',
    lastOutcome: 'Last outcome',
    nextCall: 'Next call',
    logThisCall: 'Log this call',
    onboardToLead: 'Onboard to Lead',
    wentCold: 'Went cold',
    lastStage: 'Last stage',
    reason: 'Reason',
    reopen: 'Re-open · call',
    noProspects: 'No prospects yet — import a list to start.',
    noQueue: 'Nothing queued. Add prospects from the first tab.',
    noCold: 'Nothing has gone cold. Unusual, and good.',
    added: 'Added {n} · skipped {s} already known',

    // call form
    logCallTitle: 'Log this call',
    outcome: 'Outcome',
    interestLevel: 'Interest level',
    callDuration: 'Call duration',
    whoYouSpokeTo: 'Who you spoke to',
    name: 'Name',
    roleTitle: 'Role / title',
    directLine: 'Direct line / mobile',
    currentVendor: 'Current agency / vendor',
    budgetSignal: 'Budget signal',
    servicesNeeded: 'Services they need',
    buyingWindow: 'Buying window',
    painPoint: 'Pain point / what they said',
    objectionRaised: 'Objection raised',
    whatNext: 'What happens next',
    stepFollowUp: 'Schedule follow-up call',
    stepOnboard: 'Onboard to Leads',
    stepMeeting: 'Schedule meeting',
    stepNone: 'Nothing yet',
    purpose: 'Purpose of the call',
    assignOwner: 'Assign sales owner',
    entity: 'Entity',
    format: 'Format',
    theirAttendees: 'Their attendees',
    ourAttendees: 'Bring from our side',
    invitesNote: 'Sends a calendar invite and creates a meeting task.',
    saveCall: 'Save call',
    now: 'Now / this month',
    thisQuarter: 'This quarter',
    nextQuarter: 'Next quarter',
    nextFy: 'Next fiscal year',
    unknown: 'Unknown',

    // graduate
    graduateTitle: 'Graduate to Account',
    was: 'Was',
    becomes: 'Becomes',
    salesOwns: 'Sales owns it',
    aeOwnsNow: 'AE owns it · Sales stays close',
    aeTakingOver: 'AE taking over',
    salesStaysAs: 'Sales stays as',
    relationshipOwner: 'Relationship owner',
    upsellSupport: 'Upsell support only',
    fullyHandedOver: 'Fully handed over',
    engagementType: 'Engagement type',
    oneOff: 'One-off campaign',
    retainer: 'Monthly retainer',
    mediaBuy: 'Media buy · dated window',
    projectScope: 'Project · fixed scope',
    serviceStart: 'Service start',
    contractedValue: 'Contracted value',
    reviewCadence: 'Review cadence',
    weekly: 'Weekly check-in',
    monthly: 'Monthly report',
    quarterly: 'Quarterly business review',
    milestone: 'On milestones only',
    handoverChecklist: 'What Sales must pass to the AE',
    handoverNotes: 'Anything the AE should know',
    graduated: 'Graduated. {n} now owns it.',

    // accounts
    account: 'Account',
    health: 'Health',
    activeWork: 'Active work',
    nextActionCol: 'Next action',
    lastTouch: 'Last touch',
    since: 'Since',
    good: 'Good',
    watch: 'Watch',
    atRisk: 'At risk',
    accountsBlurb: 'Won clients, now owned by an AE — servicing, health and growth',
    dueOnly: 'Due a touch',
    noAccountsYet: 'No accounts yet — graduate a won deal.',
    logServiceTitle: 'Log service',
    whatHappened: 'What happened',
    setHealth: 'Health after this',
    setNextAction: 'Next action',
    by: 'by',
    serviceLogged: 'Service logged.',
    untouchedFor: 'untouched {n}d',

    // --- shared by the three management screens (F3) ---
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    attainment: 'Attainment',
    winRate: 'Win rate',
    dealsWon: 'Deals won',
    dealsLost: 'Deals lost',
    callsLogged: 'Calls',
    briefsIn: 'Briefs',
    meetings: 'Meetings',
    quotesOut: 'Quotes',
    avgDeal: 'Avg deal',
    avgCycle: 'Avg cycle',
    nDays: '{n}d',
    pipelineCreated: 'Pipeline created',
    total: 'Total',
    person: 'Person',
    role: 'Role',
    entities: 'Entities',

    // --- monthly review ---
    reviewBlurb: 'What each person closed against what they were asked to close.',
    teamTotal: 'Team total',
    monthStillRunning: 'This month is still running — figures move until it closes.',
    noReviewRows: 'Nobody to review for this month.',
    publishMonth: 'Publish month',
    reopenMonth: 'Reopen month',
    publishNotFinished: 'Publish a month once it has finished.',
    monthPublished: 'Month published.',
    monthReopened: 'Month reopened for editing.',
    published: 'Published',
    submitted: 'Submitted',
    draft: 'Draft',
    publishedBy: 'Published by {name}',
    ofQuotaShort: 'of {q}',
    vsPrev: 'vs {p}',
    reviewNote: 'Review note',
    notePlaceholder: 'What went well, what got in the way, what changes next month.',
    saveDraft: 'Save draft',
    submitReview: 'Submit',
    reviewSubmitted: 'Review submitted.',
    noteSaved: 'Note saved.',
    unsaved: 'Unsaved',
    noNoteWritten: 'No note written.',

    // --- insights ---
    insightsBlurb: 'Where deals stall, who is closing, why we lose, and what is going quiet.',
    allEntities: 'Both',
    noEntityAccess: 'You have no entity access, so there is nothing to show.',
    wonCol: 'Won',
    valueLost: 'Lost',
    avgWinDays: 'Avg days to win',
    funnelTitle: 'Funnel',
    // The rollup has no `kind` column, so this really is new-business only.
    // Better said on the card than discovered in a review.
    funnelNewOnly: 'New-customer deals only — the upsell board uses different stages.',
    noFunnelData: 'No deals opened in this month yet.',
    leaderboard: 'Leaderboard',
    noClosedYet: 'Nothing closed this month yet.',
    rep: 'Rep',
    vsPrevShort: 'vs prev',
    lossReasons: 'Why we lost',
    noLosses: 'No losses this month.',
    lostTo: 'Lost to',
    unassigned: 'Unassigned work',
    unassignedOne: 'Unassigned',
    accountsNoAe: 'Accounts with no AE',
    dealsNoOwner: 'Deals with no owner',
    prospectsNoOwner: 'Prospects with no owner',
    noNextAction: 'Accounts with no next action',
    idleDeals: 'Idle deals',
    coldAfter: 'Untouched for more than {n} days.',
    nothingIdle: 'Everything is moving.',
    untouchedAccounts: 'Untouched accounts',
    allTouched: 'Every account has been touched.',
    ungraduatedWins: 'Won but not graduated',
    ungraduatedBlurb: 'Won deals with nobody yet holding the relationship.',
    allGraduated: 'Every win has been graduated.',
    wonOn: 'won',
    generatedAt: 'Generated {d}',

    // --- admin ---
    adminBlurb: 'Users, pipeline rules, quotas and integrations.',
    adminOnly: 'This screen is for admins only.',
    rebuildFigures: 'Rebuild figures',
    rebuilding: 'Rebuilding…',
    rebuildHint: 'Recompute this month’s rollups now instead of waiting for the cache to expire.',
    figuresRebuilt: 'Figures rebuilt in {ms}ms.',
    tabUsers: 'Users',
    tabRules: 'Pipeline rules',
    tabQuotas: 'Quotas',
    tabTargets: 'Targets',
    tabConnections: 'Connections',
    tabExports: 'Exports',
    tabAudit: 'Audit trail',

    newUser: 'Add someone',
    newUserBlurb: 'They sign in with Google — no password to set.',
    newUserNote:
      'They can sign in as soon as this is saved, using the Google account on that address.',
    createUser: 'Add them',
    userCreated: 'Added.',
    userDeleted: 'Removed.',
    fullName: 'Full name',
    thaiName: 'Thai name',
    mobile: 'Mobile',
    startDate: 'Start date',
    reportsTo: 'Reports to',
    records: 'Records',
    lastLogin: 'Last sign-in',
    never: 'Never',
    you: 'you',
    deactivate: 'Deactivate',
    reactivate: 'Reactivate',
    delete: 'Delete',
    confirmDelete: 'Really delete?',

    rulesBlurb:
      'These drive every board and form. Changes take effect for everyone immediately.',
    editedBy: 'edited by {name}',
    needsANumber: 'That needs to be a number.',

    quotasBlurb: 'Set in baht, per person, per month.',
    saveQuotas: 'Save quotas',
    quotasSaved: 'Quotas saved.',
    quotaNew: 'New customer',
    quotaUpsell: 'Upsell',

    targetsBlurb: 'The annual number each house is working to.',
    annualTarget: 'Annual target (baht)',
    wonThisYear: 'won this year',
    noTargetSet: 'No target set',
    targetSaved: 'Target saved.',

    connectionsBlurb:
      'Status is what the last sync reported. Credentials live in the Worker and never reach this screen.',
    credsPresent: 'Credentials set',
    credsMissing: 'No credentials',
    lastSync: 'Last sync',

    exportsBlurb: 'CSV with a UTF-8 BOM and money in baht — opens correctly in Excel.',
    nColumns: '{n} columns',
    downloadCsv: 'Download CSV',

    auditBlurb: 'The last 150 changes anyone made.',
    noAuditYet: 'Nothing has been changed yet.',
    when: 'When',
    who: 'Who',
    target: 'Target',
    system: 'System',

    // --- notifications (B4) ---
    notifications: 'Notifications',
    markAllRead: 'Mark all read',
    noNotifications: 'Nothing new.',

    // --- scheduled work (B4) ---
    tabJobs: 'Jobs',
    jobsBlurb:
      'Background work runs on a schedule with nobody watching, so every run is recorded here.',
    cronNotConfigured:
      'No job secret is set, so the cron Worker cannot run anything. Jobs can still be run by hand from here.',
    job: 'Job',
    runNow: 'Run now',
    running: 'Running…',
    manualRun: 'run by hand',
    neverRun: 'Never run',
    jobSucceeded: 'OK',
    jobFailedShort: 'Failed',
    // "Started but never finished" — the isolate died before it could record an
    // outcome. Worth its own word: it is not a failure the job reported.
    jobIncomplete: 'Did not finish',
    jobOk: 'Job finished in {ms}ms.',
    jobFailed: 'Job failed — {detail}',
    result: 'Result',
    duration: 'Took',
    detail: 'Detail',

    loading: 'Loading…',
    retry: 'Retry',
    somethingWrong: 'Something went wrong.',
  },

  th: {
    search: 'ค้นหา',
    logDeal: 'บันทึกดีล',
    logService: 'บันทึกงานบริการ',
    quickLog: 'บันทึกด่วน',
    signOut: 'ออกจากระบบ',
    language: 'ภาษา',
    appearance: 'ธีม',
    accent: 'สีหลัก',
    viewAs: 'ดูในมุมของ',
    light: 'สว่าง',
    dark: 'มืด',
    compact: 'กระชับ',
    comfortable: 'สบายตา',
    viewOnly: 'ดูอย่างเดียว',

    navToday: 'วันนี้',
    navDeals: 'ดีลลูกค้าใหม่',
    navCalls: 'โทรหาลูกค้าใหม่',
    navClients: 'ลีด',
    navAccounts: 'ลูกค้าปัจจุบัน',
    navUpsells: 'ดีลต่อยอด',
    navReview: 'รีวิวรายเดือน',
    navInsights: 'ข้อมูลเชิงลึก',
    navAdmin: 'ผู้ดูแลระบบ',

    signIn: 'เข้าสู่ระบบ',
    signInBlurb: 'ใช้บัญชี Google ของ brandstorm.co.th, brandstorm.global หรือ motionreach.co.th',
    sessionNotKept:
      'Google ยืนยันตัวตนสำเร็จ แต่เบราว์เซอร์นี้ไม่ได้เก็บคุกกี้เซสชันไว้ กรุณาตรวจสอบว่าอนุญาตคุกกี้สำหรับ crm.motionreach.co.th แล้วลองอีกครั้ง',
    continueGoogle: 'เข้าสู่ระบบด้วย Google',
    or: 'หรือ',
    workEmail: 'อีเมลบริษัท',
    magicLink: 'ส่งลิงก์เข้าสู่ระบบทางอีเมล',
    magicNote: 'ลิงก์นี้ใช้ได้กับบัญชีนอก Workspace เซสชันอยู่ได้ 30 วัน',
    magicSent: 'ถ้าอีเมลนี้อยู่ในระบบ ลิงก์กำลังส่งไปแล้ว',
    tagline: 'ทั้งบริษัทคือกรวยการขายเดียวกัน',
    taglineSub: 'ไปป์ไลน์เดียวสำหรับสองบริษัท — บรีฟเข้า รายได้ออก',

    morning: 'สวัสดี',
    myAttainment: 'ผลงานของฉัน',
    teamAttainment: 'ผลงานทีม',
    monthlyReview: 'รีวิวรายเดือน',
    ofQuota: 'จากเป้า {q}',
    noQuota: 'ยังไม่ได้ตั้งเป้า',
    newCust: 'ลูกค้าใหม่',
    upsell: 'ต่อยอด',
    toGraduate: 'ปิดได้แล้ว — รอส่งต่อให้ AE',
    graduate: 'ส่งต่อให้ AE',
    newBriefs: 'บรีฟใหม่',
    createDeal: 'สร้างดีล',
    briefsZero: 'ครบแล้ว — ทุกบรีฟมีดีลแล้ว',
    callsDue: 'ต้องโทรวันนี้',
    logCall: 'บันทึกการโทร',
    needsAttention: 'ต้องรีบดู',
    servicingDue: 'ถึงรอบดูแลลูกค้า',
    myTasks: 'งานของฉัน',
    idle: 'เงียบมา {n} วัน',
    nothingWaiting: 'ยังไม่มีดีลรอส่งต่อ',
    noCallsDue: 'ยังไม่มีคิวโทร ไปหาลูกค้าใหม่กันเถอะ',
    allMoving: 'ทุกดีลยังเดินอยู่ ดีมาก',
    noServicing: 'วันนี้ยังไม่มีลูกค้าที่ต้องดูแล',
    tasksClear: 'ไม่มีงานค้าง',

    board: 'บอร์ด',
    table: 'ตาราง',
    allOwners: 'ทุกคน',
    allTiers: 'ทุกระดับ',
    openPipeline: 'ไปป์ไลน์ที่เปิดอยู่',
    deal: 'ดีล',
    titleClient: 'ชื่อดีล · ลูกค้า',
    salesRep: 'เซลส์',
    amount: 'มูลค่า',
    close: 'กำหนดปิด',
    stage: 'ขั้นตอน',
    noDeals: 'ยังไม่มีดีลตรงนี้ — ลากเข้ามาสักอัน',
    moveTo: 'ย้ายไปที่',
    lossReason: 'ทำไมถึงไม่ได้งาน',
    lossRequired: 'ต้องระบุเหตุผลที่เสียดีล',
    competitor: 'คู่แข่ง',
    cancel: 'ยกเลิก',
    save: 'บันทึก',
    markLost: 'บันทึกว่าเสียดีล',

    overview: 'ภาพรวม',
    activity: 'ความเคลื่อนไหว',
    quotes: 'ใบเสนอราคา',
    contacts: 'ผู้ติดต่อ',
    projects: 'โปรเจกต์',
    tasks: 'งาน',
    owner: 'ผู้ดูแล',
    aeOwner: 'AE',
    contact: 'ผู้ติดต่อหลัก',
    source: 'ที่มา',
    tier: 'ระดับ',
    weighted: 'มูลค่าถ่วงน้ำหนัก',
    industry: 'อุตสาหกรรม',
    website: 'เว็บไซต์',
    lastActivity: 'ความเคลื่อนไหวล่าสุด',
    noActivity: 'ยังไม่มีการบันทึก',
    noQuotes: 'ยังไม่มีเอกสารจาก FlowAccount',
    noContacts: 'ยังไม่มีผู้ติดต่อ',
    openDeals: 'ดีลที่เปิดอยู่',
    revenueYtd: 'รายได้ปีนี้',

    logActivity: 'บันทึกความเคลื่อนไหว',
    type: 'ประเภท',
    call: 'โทร',
    meet: 'ประชุม',
    email: 'อีเมล',
    note: 'โน้ต',
    line: 'LINE',
    subject: 'เกิดอะไรขึ้น',
    details: 'รายละเอียด',
    against: 'เกี่ยวกับ',
    followUp: 'งานติดตามผล',
    followUpPh: 'เช่น ส่งมีเดียคิทให้ลูกค้า',
    due: 'กำหนดส่ง',
    logIt: 'บันทึก',
    logged: 'บันทึกแล้ว',
    saved: 'บันทึกแล้ว',
    moved: 'ย้ายไป {s} แล้ว',
    taskDone: 'ปิดงานแล้ว',
    pickTarget: 'เลือกดีลหรือบริษัทก่อน',

    tabProspects: 'รายชื่อ',
    tabQueue: 'คิวโทร',
    tabCold: 'เงียบไปแล้ว',
    coldBlurb: 'หาลูกค้าใหม่ — หารายชื่อ โทร แล้วปิด',
    sources: 'แหล่งที่มา',
    allIndustries: 'ทุกอุตสาหกรรม',
    importList: 'นำเข้ารายชื่อ',
    importBlurb:
      'วางชื่อบริษัทบรรทัดละหนึ่งราย หรือ CSV: ชื่อ, อุตสาหกรรม, เบอร์, ผู้ติดต่อ — LinkedIn กับรายชื่อจากงานอีเวนต์ต้องส่งออกมาเอง ไม่มี API ให้ดึงสด',
    generate: 'เพิ่มเข้ารายชื่อ',
    excludesExisting: 'ตัดคนที่เป็นลูกค้าอยู่แล้วหรือมีดีลค้างอยู่ออกให้',
    company: 'บริษัท',
    fit: 'ความเหมาะสม',
    whyNow: 'สัญญาณ — ทำไมต้องตอนนี้',
    contactFound: 'ผู้ติดต่อที่หาเจอ',
    action: 'ดำเนินการ',
    addToQueue: 'เพิ่มเข้าคิว',
    notAFit: 'ไม่ใช่กลุ่มเป้าหมาย',
    attempts: 'โทรไปแล้ว',
    lastOutcome: 'ผลครั้งล่าสุด',
    nextCall: 'โทรครั้งต่อไป',
    logThisCall: 'บันทึกการโทร',
    onboardToLead: 'เปลี่ยนเป็นลีด',
    wentCold: 'เงียบไปเมื่อ',
    lastStage: 'ขั้นตอนสุดท้าย',
    reason: 'เหตุผล',
    reopen: 'เปิดใหม่ · โทร',
    noProspects: 'ยังไม่มีรายชื่อ — นำเข้ารายชื่อเพื่อเริ่ม',
    noQueue: 'ยังไม่มีคิว เพิ่มจากแท็บแรกได้เลย',
    noCold: 'ยังไม่มีดีลที่เงียบไป ดีมาก',
    added: 'เพิ่ม {n} รายการ · ข้าม {s} รายการที่มีอยู่แล้ว',

    logCallTitle: 'บันทึกการโทรครั้งนี้',
    outcome: 'ผลการโทร',
    interestLevel: 'ระดับความสนใจ',
    callDuration: 'ระยะเวลาโทร',
    whoYouSpokeTo: 'คุยกับใคร',
    name: 'ชื่อ',
    roleTitle: 'ตำแหน่ง',
    directLine: 'เบอร์ตรง / มือถือ',
    currentVendor: 'เอเจนซี่ / ผู้ให้บริการปัจจุบัน',
    budgetSignal: 'สัญญาณงบประมาณ',
    servicesNeeded: 'บริการที่เขาต้องการ',
    buyingWindow: 'ช่วงเวลาที่จะซื้อ',
    painPoint: 'ปัญหา / สิ่งที่เขาพูด',
    objectionRaised: 'ข้อโต้แย้ง',
    whatNext: 'ขั้นต่อไป',
    stepFollowUp: 'นัดโทรติดตาม',
    stepOnboard: 'เปลี่ยนเป็นลีด',
    stepMeeting: 'นัดประชุม',
    stepNone: 'ยังไม่มี',
    purpose: 'จุดประสงค์การโทร',
    assignOwner: 'มอบหมายให้เซลส์',
    entity: 'บริษัท',
    format: 'รูปแบบ',
    theirAttendees: 'ผู้เข้าร่วมฝั่งลูกค้า',
    ourAttendees: 'ฝั่งเราไปใคร',
    invitesNote: 'ระบบจะส่งคำเชิญปฏิทินและสร้างงานประชุมให้',
    saveCall: 'บันทึก',
    now: 'ตอนนี้ / เดือนนี้',
    thisQuarter: 'ไตรมาสนี้',
    nextQuarter: 'ไตรมาสหน้า',
    nextFy: 'ปีงบหน้า',
    unknown: 'ไม่ทราบ',

    graduateTitle: 'ส่งต่อเป็นลูกค้าประจำ',
    was: 'เดิม',
    becomes: 'เปลี่ยนเป็น',
    salesOwns: 'เซลส์ดูแล',
    aeOwnsNow: 'AE ดูแล · เซลส์ยังอยู่ใกล้',
    aeTakingOver: 'AE ที่รับช่วงต่อ',
    salesStaysAs: 'เซลส์ยังอยู่ในฐานะ',
    relationshipOwner: 'ผู้ดูแลความสัมพันธ์',
    upsellSupport: 'ช่วยเฉพาะงานต่อยอด',
    fullyHandedOver: 'ส่งมอบทั้งหมด',
    engagementType: 'รูปแบบงาน',
    oneOff: 'แคมเปญครั้งเดียว',
    retainer: 'รีเทนเนอร์รายเดือน',
    mediaBuy: 'ซื้อสื่อ · ช่วงเวลาที่กำหนด',
    projectScope: 'โปรเจกต์ · ขอบเขตตายตัว',
    serviceStart: 'เริ่มให้บริการ',
    contractedValue: 'มูลค่าตามสัญญา',
    reviewCadence: 'รอบการรีวิว',
    weekly: 'เช็คอินรายสัปดาห์',
    monthly: 'รายงานรายเดือน',
    quarterly: 'รีวิวธุรกิจรายไตรมาส',
    milestone: 'เฉพาะตอนถึงหมุดหมาย',
    handoverChecklist: 'สิ่งที่เซลส์ต้องส่งต่อให้ AE',
    handoverNotes: 'เรื่องที่ AE ควรรู้',
    graduated: 'ส่งต่อแล้ว — {n} เป็นผู้ดูแล',

    account: 'ลูกค้า',
    health: 'สุขภาพความสัมพันธ์',
    activeWork: 'งานที่กำลังทำ',
    nextActionCol: 'สิ่งที่ต้องทำต่อ',
    lastTouch: 'ติดต่อล่าสุด',
    since: 'เป็นลูกค้าตั้งแต่',
    good: 'ดี',
    watch: 'ต้องจับตา',
    atRisk: 'เสี่ยง',
    accountsBlurb: 'ลูกค้าที่ปิดได้แล้ว AE เป็นผู้ดูแล — บริการ สุขภาพ และการเติบโต',
    dueOnly: 'ถึงรอบต้องดูแล',
    noAccountsYet: 'ยังไม่มีลูกค้าประจำ — ส่งต่อดีลที่ปิดได้ก่อน',
    logServiceTitle: 'บันทึกงานบริการ',
    whatHappened: 'เกิดอะไรขึ้น',
    setHealth: 'สุขภาพหลังจากนี้',
    setNextAction: 'สิ่งที่ต้องทำต่อ',
    by: 'ภายใน',
    serviceLogged: 'บันทึกแล้ว',
    untouchedFor: 'ไม่ได้ติดต่อมา {n} วัน',

    // --- ใช้ร่วมกันทั้งสามหน้าจัดการ (F3) ---
    prevMonth: 'เดือนก่อนหน้า',
    nextMonth: 'เดือนถัดไป',
    attainment: 'เทียบเป้า',
    winRate: 'อัตราปิดดีล',
    dealsWon: 'ดีลที่ปิดได้',
    dealsLost: 'ดีลที่เสีย',
    callsLogged: 'การโทร',
    briefsIn: 'บรีฟ',
    meetings: 'ประชุม',
    quotesOut: 'ใบเสนอราคา',
    avgDeal: 'มูลค่าดีลเฉลี่ย',
    avgCycle: 'ระยะเวลาปิดเฉลี่ย',
    nDays: '{n} วัน',
    pipelineCreated: 'ไปป์ไลน์ที่สร้าง',
    total: 'รวม',
    person: 'ชื่อ',
    role: 'บทบาท',
    entities: 'บริษัท',

    // --- รีวิวรายเดือน ---
    reviewBlurb: 'ผลงานที่แต่ละคนปิดได้ เทียบกับเป้าที่ตั้งไว้',
    teamTotal: 'รวมทั้งทีม',
    monthStillRunning: 'เดือนนี้ยังไม่จบ ตัวเลขยังเปลี่ยนได้จนกว่าจะสิ้นเดือน',
    noReviewRows: 'ยังไม่มีใครให้รีวิวในเดือนนี้',
    publishMonth: 'ประกาศผลเดือนนี้',
    reopenMonth: 'เปิดให้แก้ไขอีกครั้ง',
    publishNotFinished: 'ประกาศผลได้เมื่อเดือนนั้นจบแล้ว',
    monthPublished: 'ประกาศผลเรียบร้อย',
    monthReopened: 'เปิดให้แก้ไขแล้ว',
    published: 'ประกาศแล้ว',
    submitted: 'ส่งแล้ว',
    draft: 'ฉบับร่าง',
    publishedBy: 'ประกาศโดย {name}',
    ofQuotaShort: 'จาก {q}',
    vsPrev: 'เทียบ {p}',
    reviewNote: 'บันทึกรีวิว',
    notePlaceholder: 'อะไรที่ไปได้ดี อะไรที่เป็นอุปสรรค และเดือนหน้าจะปรับอะไร',
    saveDraft: 'บันทึกร่าง',
    submitReview: 'ส่งรีวิว',
    reviewSubmitted: 'ส่งรีวิวแล้ว',
    noteSaved: 'บันทึกแล้ว',
    unsaved: 'ยังไม่บันทึก',
    noNoteWritten: 'ยังไม่ได้เขียนบันทึก',

    // --- อินไซต์ ---
    insightsBlurb: 'ดีลติดตรงไหน ใครปิดได้ เราเสียเพราะอะไร และอะไรกำลังเงียบ',
    allEntities: 'ทั้งสอง',
    noEntityAccess: 'คุณยังไม่ได้รับสิทธิ์เข้าถึงบริษัทใด จึงยังไม่มีข้อมูลแสดง',
    wonCol: 'ปิดได้',
    valueLost: 'เสียไป',
    avgWinDays: 'วันเฉลี่ยกว่าจะปิดได้',
    funnelTitle: 'ฟันเนล',
    funnelNewOnly: 'นับเฉพาะดีลลูกค้าใหม่ — บอร์ดอัปเซลใช้สเตจคนละชุด',
    noFunnelData: 'เดือนนี้ยังไม่มีดีลเปิดใหม่',
    leaderboard: 'ตารางอันดับ',
    noClosedYet: 'เดือนนี้ยังไม่มีดีลที่ปิด',
    rep: 'เซลส์',
    vsPrevShort: 'เทียบเดือนก่อน',
    lossReasons: 'เหตุผลที่เสียดีล',
    noLosses: 'เดือนนี้ไม่มีดีลที่เสีย',
    lostTo: 'เสียให้',
    unassigned: 'งานที่ยังไม่มีเจ้าของ',
    unassignedOne: 'ยังไม่มีเจ้าของ',
    accountsNoAe: 'ลูกค้าที่ยังไม่มี AE',
    dealsNoOwner: 'ดีลที่ยังไม่มีเจ้าของ',
    prospectsNoOwner: 'รายชื่อที่ยังไม่มีเจ้าของ',
    noNextAction: 'ลูกค้าที่ยังไม่ระบุงานถัดไป',
    idleDeals: 'ดีลที่นิ่ง',
    coldAfter: 'ไม่มีความเคลื่อนไหวเกิน {n} วัน',
    nothingIdle: 'ทุกดีลยังเดินอยู่',
    untouchedAccounts: 'ลูกค้าที่ไม่ได้ติดต่อ',
    allTouched: 'ติดต่อลูกค้าครบทุกรายแล้ว',
    ungraduatedWins: 'ปิดได้แล้วแต่ยังไม่ส่งต่อ',
    ungraduatedBlurb: 'ดีลที่ปิดได้แล้ว แต่ยังไม่มีใครรับดูแลต่อ',
    allGraduated: 'ส่งต่อครบทุกดีลแล้ว',
    wonOn: 'ปิดเมื่อ',
    generatedAt: 'ข้อมูล ณ {d}',

    // --- ผู้ดูแลระบบ ---
    adminBlurb: 'ผู้ใช้ กติกาไปป์ไลน์ เป้า และการเชื่อมต่อระบบ',
    adminOnly: 'หน้านี้สำหรับผู้ดูแลระบบเท่านั้น',
    rebuildFigures: 'คำนวณตัวเลขใหม่',
    rebuilding: 'กำลังคำนวณ…',
    rebuildHint: 'คำนวณสรุปของเดือนนี้ใหม่ทันที ไม่ต้องรอแคชหมดอายุ',
    figuresRebuilt: 'คำนวณใหม่เสร็จใน {ms} มิลลิวินาที',
    tabUsers: 'ผู้ใช้',
    tabRules: 'กติกาไปป์ไลน์',
    tabQuotas: 'เป้ารายคน',
    tabTargets: 'เป้ารายปี',
    tabConnections: 'การเชื่อมต่อ',
    tabExports: 'ส่งออกข้อมูล',
    tabAudit: 'ประวัติการแก้ไข',

    newUser: 'เพิ่มคน',
    newUserBlurb: 'เข้าใช้งานด้วยบัญชี Google ไม่ต้องตั้งรหัสผ่าน',
    newUserNote: 'เมื่อบันทึกแล้ว เขาเข้าใช้งานได้ทันทีด้วยบัญชี Google ของอีเมลนั้น',
    createUser: 'เพิ่ม',
    userCreated: 'เพิ่มแล้ว',
    userDeleted: 'ลบแล้ว',
    fullName: 'ชื่อ-นามสกุล',
    thaiName: 'ชื่อภาษาไทย',
    mobile: 'เบอร์มือถือ',
    startDate: 'วันเริ่มงาน',
    reportsTo: 'รายงานต่อ',
    records: 'ข้อมูลที่ถือ',
    lastLogin: 'เข้าใช้ล่าสุด',
    never: 'ยังไม่เคย',
    you: 'คุณ',
    deactivate: 'ปิดการใช้งาน',
    reactivate: 'เปิดการใช้งาน',
    delete: 'ลบ',
    confirmDelete: 'ลบจริงหรือไม่',

    rulesBlurb: 'ค่าเหล่านี้ควบคุมทุกบอร์ดและทุกฟอร์ม แก้แล้วมีผลกับทุกคนทันที',
    editedBy: 'แก้ไขโดย {name}',
    needsANumber: 'ช่องนี้ต้องเป็นตัวเลข',

    quotasBlurb: 'ตั้งเป็นบาท รายคน รายเดือน',
    saveQuotas: 'บันทึกเป้า',
    quotasSaved: 'บันทึกเป้าแล้ว',
    quotaNew: 'ลูกค้าใหม่',
    quotaUpsell: 'อัปเซล',

    targetsBlurb: 'เป้ารายปีของแต่ละบริษัท',
    annualTarget: 'เป้ารายปี (บาท)',
    wonThisYear: 'ปิดได้ในปีนี้',
    noTargetSet: 'ยังไม่ได้ตั้งเป้า',
    targetSaved: 'บันทึกเป้าแล้ว',

    connectionsBlurb:
      'สถานะคือผลจากการซิงก์ครั้งล่าสุด ส่วนคีย์ทั้งหมดเก็บอยู่ใน Worker ไม่ถูกส่งมาที่หน้านี้',
    credsPresent: 'ตั้งค่าคีย์แล้ว',
    credsMissing: 'ยังไม่มีคีย์',
    lastSync: 'ซิงก์ล่าสุด',

    exportsBlurb: 'ไฟล์ CSV มี UTF-8 BOM และตัวเลขเป็นบาท เปิดใน Excel ได้ถูกต้อง',
    nColumns: '{n} คอลัมน์',
    downloadCsv: 'ดาวน์โหลด CSV',

    auditBlurb: 'การแก้ไข 150 รายการล่าสุด',
    noAuditYet: 'ยังไม่มีการแก้ไข',
    when: 'เมื่อไร',
    who: 'ใคร',
    target: 'รายการที่แก้',
    system: 'ระบบ',

    // --- การแจ้งเตือน (B4) ---
    notifications: 'การแจ้งเตือน',
    markAllRead: 'อ่านทั้งหมดแล้ว',
    noNotifications: 'ยังไม่มีอะไรใหม่',

    // --- งานตามกำหนดเวลา (B4) ---
    tabJobs: 'งานอัตโนมัติ',
    jobsBlurb: 'งานเบื้องหลังทำงานตามเวลาที่ตั้งไว้โดยไม่มีใครเฝ้า ทุกครั้งที่รันจึงถูกบันทึกไว้ที่นี่',
    cronNotConfigured:
      'ยังไม่ได้ตั้งค่า job secret ตัว cron Worker จึงยังรันงานไม่ได้ แต่สั่งรันเองจากหน้านี้ได้',
    job: 'งาน',
    runNow: 'สั่งรันเลย',
    running: 'กำลังรัน…',
    manualRun: 'สั่งรันเอง',
    neverRun: 'ยังไม่เคยรัน',
    jobSucceeded: 'สำเร็จ',
    jobFailedShort: 'ล้มเหลว',
    jobIncomplete: 'รันไม่จบ',
    jobOk: 'ทำงานเสร็จใน {ms} มิลลิวินาที',
    jobFailed: 'งานล้มเหลว — {detail}',
    result: 'ผลลัพธ์',
    duration: 'ใช้เวลา',
    detail: 'รายละเอียด',

    loading: 'กำลังโหลด…',
    retry: 'ลองใหม่',
    somethingWrong: 'มีบางอย่างผิดพลาด',
  },
};

const LangCtx = createContext({ lang: 'en', t: (k) => k, setLang: () => {} });

export function LangProvider({ initial = 'en', onChange, children }) {
  const [lang, setLangState] = useState(initial);

  const t = useCallback(
    (key, vars) => {
      let s = LANG[lang]?.[key] ?? LANG.en[key] ?? key;
      if (vars) for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, v);
      return s;
    },
    [lang],
  );

  const setLang = useCallback(
    (next) => {
      setLangState(next);
      document.documentElement.lang = next;
      onChange?.(next);
    },
    [onChange],
  );

  // createElement rather than JSX so this file can stay a .js — Vite only runs
  // the JSX transform on .jsx/.tsx.
  return React.createElement(LangCtx.Provider, { value: { lang, t, setLang } }, children);
}

export const useLang = () => useContext(LangCtx);
